/**
 * Date Formatting Utilities
 *
 * Centralized date formatting functions for consistent display across the app.
 */

// =============================================================================
// BASIC FORMATTERS
// =============================================================================

/**
 * Format date to "MMM YYYY" format (e.g., "Jan 2024")
 * Used for displaying post dates on cards and detail pages.
 */
export function formatDate(date: Date | null | undefined): string {
  if (!date) return '';
  return new Intl.DateTimeFormat('en-US', {
    year: 'numeric',
    month: 'short',
  }).format(date);
}

/**
 * Format date to full date format (e.g., "January 15, 2024")
 */
export function formatDateFull(date: Date | null | undefined): string {
  if (!date) return '';
  return new Intl.DateTimeFormat('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  }).format(date);
}

/**
 * Format date to ISO string for forms and APIs
 */
export function formatDateISO(date: Date | null | undefined): string {
  if (!date) return '';
  return date.toISOString().split('T')[0];
}

/**
 * Format date to Korean format (e.g., "2024년 1월 15일")
 */
export function formatDateKorean(date: Date | null | undefined): string {
  if (!date) return '';
  return new Intl.DateTimeFormat('ko-KR', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  }).format(date);
}

/**
 * Format date to short Korean format (e.g., "1월 15일")
 */
export function formatDateKoreanShort(date: Date | null | undefined): string {
  if (!date) return '';
  return new Intl.DateTimeFormat('ko-KR', {
    month: 'short',
    day: 'numeric',
  }).format(date);
}

// =============================================================================
// RELATIVE TIME FORMATTERS
// =============================================================================

/**
 * Format date to relative time (e.g., "방금 전", "5분 전", "3시간 전")
 * Used for displaying recent activity timestamps.
 */
export function formatRelativeTime(date: Date | null | undefined): string {
  if (!date) return '';

  const now = new Date();
  const diff = now.getTime() - date.getTime();

  const seconds = Math.floor(diff / 1000);
  const minutes = Math.floor(diff / 60000);
  const hours = Math.floor(diff / 3600000);
  const days = Math.floor(diff / 86400000);
  const weeks = Math.floor(days / 7);
  const months = Math.floor(days / 30);

  if (seconds < 60) return '방금 전';
  if (minutes < 60) return `${minutes}분 전`;
  if (hours < 24) return `${hours}시간 전`;
  if (days < 7) return `${days}일 전`;
  if (weeks < 4) return `${weeks}주 전`;
  if (months < 12) return `${months}개월 전`;

  return formatDateKoreanShort(date);
}

/**
 * Format date to relative time in English
 */
export function formatRelativeTimeEN(date: Date | null | undefined): string {
  if (!date) return '';

  const now = new Date();
  const diff = now.getTime() - date.getTime();

  const seconds = Math.floor(diff / 1000);
  const minutes = Math.floor(diff / 60000);
  const hours = Math.floor(diff / 3600000);
  const days = Math.floor(diff / 86400000);
  const weeks = Math.floor(days / 7);
  const months = Math.floor(days / 30);

  if (seconds < 60) return 'just now';
  if (minutes === 1) return '1 minute ago';
  if (minutes < 60) return `${minutes} minutes ago`;
  if (hours === 1) return '1 hour ago';
  if (hours < 24) return `${hours} hours ago`;
  if (days === 1) return 'yesterday';
  if (days < 7) return `${days} days ago`;
  if (weeks === 1) return '1 week ago';
  if (weeks < 4) return `${weeks} weeks ago`;
  if (months === 1) return '1 month ago';
  if (months < 12) return `${months} months ago`;

  return formatDate(date);
}

// =============================================================================
// DATETIME FORMATTERS
// =============================================================================

/**
 * Format datetime for display (e.g., "Jan 15, 2024 at 2:30 PM")
 */
export function formatDateTime(date: Date | null | undefined): string {
  if (!date) return '';
  return new Intl.DateTimeFormat('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  }).format(date);
}

/**
 * Format datetime for Korean display (e.g., "2024년 1월 15일 오후 2:30")
 */
export function formatDateTimeKorean(date: Date | null | undefined): string {
  if (!date) return '';
  return new Intl.DateTimeFormat('ko-KR', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  }).format(date);
}

// =============================================================================
// FILE/EXPORT FORMATTERS
// =============================================================================

/**
 * Format date for filenames (e.g., "2024-01-15_143025")
 * Safe for use in file names across all operating systems.
 */
export function formatDateForFilename(date: Date = new Date()): string {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  const hours = String(date.getHours()).padStart(2, '0');
  const minutes = String(date.getMinutes()).padStart(2, '0');
  const seconds = String(date.getSeconds()).padStart(2, '0');

  return `${year}-${month}-${day}_${hours}${minutes}${seconds}`;
}

// =============================================================================
// FIRESTORE HELPERS
// =============================================================================

/**
 * Safely convert Firestore timestamp to Date
 */
export function safeToDate(
  value: unknown,
  fallback: Date = new Date()
): Date {
  if (!value) return fallback;

  // Handle Firestore Timestamp
  if (typeof value === 'object' && value !== null && 'toDate' in value) {
    return (value as { toDate: () => Date }).toDate();
  }

  // Handle Date object
  if (value instanceof Date) {
    return value;
  }

  // Handle string
  if (typeof value === 'string') {
    const parsed = new Date(value);
    return isNaN(parsed.getTime()) ? fallback : parsed;
  }

  // Handle number (timestamp)
  if (typeof value === 'number') {
    return new Date(value);
  }

  return fallback;
}
